import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const BLOCK_TYPES = [
    'paragraph',
    'heading',
    'listItem',
    'entryHeaderName',
    'entryHeaderDate',
];
const ATTRIBUTE_NAME = 'blockId';

const generateBlockId = () =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Splitting a block copies its attributes, so both halves come out carrying
// the same id. Comment excerpts are looked up by id and keep the first match,
// so a duplicate silently attaches a comment to the wrong half — re-stamp any
// id already seen earlier in the document.
const stampMissingBlockIds = (doc, tr) => {
    let nextTr = tr;
    const seen = new Set();
    doc.descendants((node, pos) => {
        if (!BLOCK_TYPES.includes(node.type.name)) return;
        const existing = node.attrs[ATTRIBUTE_NAME];
        if (existing && !seen.has(existing)) {
            seen.add(existing);
            return;
        }

        const blockId = generateBlockId();
        seen.add(blockId);
        nextTr = nextTr.setNodeAttribute(pos, ATTRIBUTE_NAME, blockId);
    });
    return nextTr;
};

export const BlockId = Extension.create({
    name: 'blockId',

    addGlobalAttributes() {
        return [
            {
                types: BLOCK_TYPES,
                attributes: {
                    [ATTRIBUTE_NAME]: {
                        default: null,
                        parseHTML: (element) =>
                            element.getAttribute('data-block-id'),
                        renderHTML: (attributes) => {
                            if (!attributes[ATTRIBUTE_NAME]) return {};
                            return {
                                'data-block-id': attributes[ATTRIBUTE_NAME],
                            };
                        },
                    },
                },
            },
        ];
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('blockId'),
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some((tr) => tr.docChanged)) {
                        return null;
                    }

                    const tr = stampMissingBlockIds(newState.doc, newState.tr);
                    return tr.steps.length ? tr : null;
                },
            }),
        ];
    },

    onCreate() {
        const { state, view } = this.editor;
        const tr = stampMissingBlockIds(state.doc, state.tr);
        if (tr.steps.length) view.dispatch(tr);
    },
});
